import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { REPORT_STATUS_LABELS, type OrganizationCategory } from '@safetyhub/shared';
import { Button, Card, DateInput, Modal } from '@safetyhub/ui';
import { adminApi, adminCompaniesApi, extractApiError, toAbsoluteApiFileUrl } from '../lib/api';
import { buildComposerApprovalData } from '../lib/composerApprovalData';
import { localDayEndIso, localDayStartIso } from '../lib/localDayBounds';
import { AdminComposedDocumentBody } from '../components/reports/AdminComposedDocumentBody';

// 문서 유형 = 양식 이름(서버가 `templateKey`로 조인해 주는 templateName). 유형 라벨 맵·유형 필터
// select를 두지 않는다 — 모든 문서가 Composer 양식이라 가를 축이 없다(사용자 웹
// `getReportTypeLabel`과 같은 규약: 코드값·키를 화면에 흘리지 않는다).
/** 타입 표시명 — 양식 이름, 없으면 중립 폴백. */
function reportTypeDisplay(r: { templateName?: string | null }): string {
  return r.templateName?.trim() || '커스텀 양식';
}

// 보고서 상태 라벨 — shared 정본(`REPORT_STATUS_LABELS`) 파생.
// 운영 콘솔 간결체라 DRAFT 외 전부 덮어쓰지만, spread가 키 전수를 보장해
// enum에 상태가 추가돼도 raw enum이 폴백으로 새지 않는다. (Inspections.tsx와 동일 규약)
const reportStatusLabels: Record<string, string> = {
  ...REPORT_STATUS_LABELS,
  CREATING: '생성중',
  PENDING_APPROVAL: '결재대기',
  APPROVED: '승인',
  REJECTED: '반려',
  CANCELLED: '취소',
};

const reportStatusColors: Record<string, string> = {
  CREATING: 'bg-gray-100 text-gray-700',
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

const REPORT_STATUS_OPTIONS = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'CREATING',
  'CANCELLED',
];

const LIMIT = 20;

/**
 * 조직 select에 담는 회사 수 — 서버 `/admin/companies`의 limit 상한(100)이 곧 천장이다.
 * 초과분은 select에 안 나오므로, 잘렸을 때는 안내 문구로 드러낸다(조용한 누락 방지).
 */
const COMPANY_SELECT_LIMIT = 100;

/**
 * 조직 option 라벨 — 동명 회사 구분(2026-08-07 사용자 확정 형식).
 *  - 개인 워크스페이스: `이름 (개인·오너명)` — 등록번호·대표자가 비어 있는 부류라 오너가 구분자
 *  - 기관: `이름 (기관)` / 일반 사업장(다수): 무표기
 *  - 공통 접미: `· N건` — 보고서 필터라는 맥락에서 0건 조직을 고르는 헛수고도 막는다
 *  - 이름 미설정 회사도 보고서를 가질 수 있어 숨기지 않는다(폴백 라벨)
 */
function companyOptionLabel(o: {
  id: string;
  name: string;
  category: OrganizationCategory;
  ownerName: string | null;
  reportCount: number;
}): string {
  const name = o.name || `(이름 없음) ${o.id.slice(0, 8)}`;
  const badge =
    o.category === 'WORKSPACE'
      ? ` (개인${o.ownerName ? `·${o.ownerName}` : ''})`
      : o.category === 'GUIDANCE_AGENCY' || o.category === 'SAFETY_MANAGEMENT_AGENCY'
        ? ' (기관)'
        : '';
  return `${name}${badge} · ${o.reportCount}건`;
}

/**
 * 날짜 표시 시간대 — 슈퍼관리자 화면은 현장/조직 시간대를 알 수 없어 운영 기준(KST)으로 고정한다.
 * (admin 다른 화면과 동일 관용. 옵션 없는 toLocale*는 ESLint가 브라우저 로캘 의존이라 경고한다.)
 */
const ADMIN_DATE_TZ = { timeZone: 'Asia/Seoul' } as const;

/** 건수 표시 포맷터 — toLocaleString() 무옵션 호출을 피한다. */
const COUNT_FORMAT = new Intl.NumberFormat('ko-KR');

export default function Reports() {
  const [page, setPage] = useState(1);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  // 필터 상태 (입력) — 적용 필터는 별도로 두어 페이지 리셋과 함께 관리
  const [companyFilter, setCompanyFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [qInput, setQInput] = useState('');
  const [qFilter, setQFilter] = useState('');

  // 조직(회사) 목록 — 필터 select용.
  // 구독 목록(`/admin/subscriptions`)이 아니라 회사 목록을 쓴다: 구독 목록은 개인
  // 작업공간(category=WORKSPACE)과 구독 없는 회사를 빼는데, 보고서는 그 회사들에도
  // 달려 있어 필터로 도달할 수 없는 조직이 생긴다. (composer PreviewContextBar와 동일 소스)
  const { data: orgData } = useQuery({
    queryKey: ['admin', 'reports', 'companies'],
    queryFn: () =>
      adminCompaniesApi.list({ limit: COMPANY_SELECT_LIMIT, sortBy: 'name', sortOrder: 'asc' }),
  });
  const organizations = orgData?.data?.items ?? [];
  const organizationTotal = orgData?.data?.total ?? 0;
  const organizationsTruncated = organizationTotal > organizations.length;

  // 현장 목록 — 조직 선택 시에만
  const { data: siteData } = useQuery({
    queryKey: ['admin', 'reports', 'sites', companyFilter],
    queryFn: () => adminApi.getSites({ limit: 200, organizationId: companyFilter }),
    enabled: !!companyFilter,
  });
  const sites = siteData?.data ?? [];

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [
      'admin',
      'reports',
      page,
      companyFilter,
      siteFilter,
      statusFilter,
      fromFilter,
      toFilter,
      qFilter,
    ],
    queryFn: () =>
      adminApi.getReports({
        page,
        limit: LIMIT,
        companyId: companyFilter || undefined,
        siteId: siteFilter || undefined,
        status: statusFilter || undefined,
        // 날짜 필터는 로컬 자정~종료일 끝(23:59:59.999)을 UTC로 변환해 경계 포함.
        // `.999`가 없으면 종료일 마지막 1초 사이에 생성된 보고서가 조회에서 누락된다.
        from: localDayStartIso(fromFilter),
        to: localDayEndIso(toFilter),
        q: qFilter || undefined,
      }),
    // 페이지 이동 시 이전 페이지 표를 유지(깜빡임 방지) — Companies와 동일 관용.
    placeholderData: (prev) => prev,
  });

  const reports = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const handleSearch = () => {
    setQFilter(qInput.trim());
    setPage(1);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">보고서 관리</h1>
        <div className="text-sm text-gray-500">총 {COUNT_FORMAT.format(total)}건</div>
      </div>

      {/* 필터 */}
      <Card radius="lg" className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">조직</label>
          <select
            value={companyFilter}
            onChange={(e) =>
              resetPageAnd(() => {
                setCompanyFilter(e.target.value);
                setSiteFilter(''); // 조직 변경 시 현장 초기화
              })
            }
            className="w-full text-sm border rounded px-2 py-1.5 bg-white"
          >
            <option value="">전체 조직</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {/* 동명 회사 구분(2026-08-07 사용자 확정: 구분·오너·보고서 수) — 개인 워크스페이스는
                    등록번호·대표자가 비어 이 셋이 실질 구분자다. 이름 미설정 회사도 보고서를 가질
                    수 있어 숨기지 않고 폴백 라벨로 노출한다. */}
                {companyOptionLabel(o)}
              </option>
            ))}
          </select>
          {organizationsTruncated && (
            <p className="mt-1 text-xs text-gray-400">
              상위 {organizations.length}곳만 표시 (총 {COUNT_FORMAT.format(organizationTotal)}곳)
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">현장</label>
          <select
            value={siteFilter}
            onChange={(e) => resetPageAnd(() => setSiteFilter(e.target.value))}
            disabled={!companyFilter}
            className="w-full text-sm border rounded px-2 py-1.5 bg-white disabled:bg-gray-100"
          >
            <option value="">{companyFilter ? '전체 현장' : '조직을 먼저 선택'}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">상태</label>
          <select
            value={statusFilter}
            onChange={(e) => resetPageAnd(() => setStatusFilter(e.target.value))}
            className="w-full text-sm border rounded px-2 py-1.5 bg-white"
          >
            <option value="">전체 상태</option>
            {REPORT_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {reportStatusLabels[s] || s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">시작일</label>
          <DateInput
            label="시작일"
            value={fromFilter}
            onChange={(next) => resetPageAnd(() => setFromFilter(next))}
            size="sm"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">종료일</label>
          <DateInput
            label="종료일"
            value={toFilter}
            onChange={(next) => resetPageAnd(() => setToFilter(next))}
            size="sm"
            className="w-full"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">제목 검색</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              placeholder="제목 일부 입력 후 Enter"
              className="flex-1 text-sm border rounded px-2 py-1.5 bg-white"
            />
            <Button variant="primary" size="sm" onClick={handleSearch}>
              검색
            </Button>
          </div>
        </div>
      </Card>

      {/* 목록 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">제목</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">조직</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">현장</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">타입</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작성자</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">생성일자</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {error ? (
              // 목록 조회 실패는 목록 영역만 대체한다 — 페이지 전체를 교체하면 필터 입력이
              // 언마운트돼 사용자가 조건을 처음부터 다시 넣어야 한다.
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center">
                  <p className="text-sm text-red-600">
                    목록을 불러오지 못했습니다: {extractApiError(error).message}
                  </p>
                  <button
                    onClick={() => void refetch()}
                    disabled={isFetching}
                    className="mt-3 px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isFetching ? '다시 시도 중...' : '다시 시도'}
                  </button>
                </td>
              </tr>
            ) : isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">로딩 중...</td>
              </tr>
            ) : reports.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">보고서가 없습니다.</td>
              </tr>
            ) : (
              reports.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedReportId(r.id)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">{r.title}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{r.company?.name ?? '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{r.site?.name ?? '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {reportTypeDisplay(r)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        reportStatusColors[r.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {reportStatusLabels[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{r.authorName ?? '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {new Date(r.createdAt).toLocaleDateString('ko-KR', ADMIN_DATE_TZ)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!error && totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </Card>

      {selectedReportId && (
        <ReportDetailModal reportId={selectedReportId} onClose={() => setSelectedReportId(null)} />
      )}
    </div>
  );
}

function ReportDetailModal({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'report', reportId],
    queryFn: () => adminApi.getReport(reportId),
  });

  const report = data?.data;

  // 결재란 표시 입력 — 매 렌더 새 객체가 되면 본문 RenderData가 매번 재조립되므로 report 기준 메모.
  const approvalData = useMemo(
    () => (report ? buildComposerApprovalData(report) : undefined),
    [report],
  );

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 종전의 sticky 헤더는 base 본문 스크롤(헤더 고정)이 맡는다.
    <Modal open onClose={onClose} title="보고서 상세" size="xl" panelClassName="!max-w-[920px]">
        <div>
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{extractApiError(error).message}</div>
          ) : report ? (
            <div className="space-y-6">
              {/* 메타 정보 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">제목</label>
                  <div className="text-sm font-semibold">{report.title}</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">타입</label>
                  <div className="text-sm">
                    {reportTypeDisplay(report)}
                    {report.templateKey && (
                      <span className="ml-1 text-xs text-gray-400">({report.templateKey})</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">조직</label>
                  <div className="text-sm">{report.company?.name ?? '-'}</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">현장</label>
                  <div className="text-sm">{report.site?.name ?? '-'}</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">작성자</label>
                  <div className="text-sm">{report.authorName ?? '-'}</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">상태</label>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      reportStatusColors[report.status] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {reportStatusLabels[report.status] || report.status}
                  </span>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">생성일시</label>
                  <div className="text-sm">
                    {new Date(report.createdAt).toLocaleString('ko-KR', ADMIN_DATE_TZ)}
                  </div>
                </div>
                {report.fileUrl && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">PDF</label>
                    <a
                      href={toAbsoluteApiFileUrl(report.fileUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-brand-600 hover:underline"
                    >
                      파일 열기
                    </a>
                  </div>
                )}
              </div>

              {/* 본문 */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">본문</h3>
                {/* 모든 문서가 Composer 양식 — 구 유형 안내 카드(「사용자 웹에서 열람」)는 라운드 3에서 걷었다. */}
                <div className="bg-gray-100 rounded-card p-4">
                  <AdminComposedDocumentBody
                    templateSnapshot={report.templateSnapshot}
                    templateKey={report.templateKey}
                    content={report.content}
                    liveData={report.composedLiveData}
                    approvalData={approvalData}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">보고서를 찾을 수 없습니다.</div>
          )}
        </div>
    </Modal>
  );
}
